import type { CreatorContent, Creator } from '@/entities/discovery';
import { usePostNavigation } from '@/features/(insight)/posts/hooks';
import { PostMedia } from '@/features/(insight)/posts/ui/social-post-card';

import { ProfileImage } from '@/shared/components/profile-image';
import { Badge } from '@/shared/components/shadcn/badge';
import { Button } from '@/shared/components/shadcn/button';
import { TooltipFlat } from '@/shared/components/shadcn/tooltip/tooltip-flat';
import { trackEvent } from '@/shared/track';
import { cn } from '@/shared/utils';
import { Eye, Heart, InfoIcon, MessageCircle, SquareArrowOutUpRight } from 'lucide-react';
import { useState } from 'react';
import { useIntersectionObserver } from 'react-simplikit';
import { getPlatformUrl } from '../../helpers/discovery-helpers';
import { PLATFORM_LOGO_BY_TYPE } from '@/entities/public-filter';

interface Props {
  creator: Creator;
  isSelected: boolean;
  searchId?: string;
  onSelect: () => void;
  onExclude?: () => void;
  onRemove?: () => void;
  selectedSeedPostIds?: string[];
  onToggleSeedPost?: (post: CreatorContent, fallbackFeatures: string[]) => void;
  seedPostLimitReached?: boolean;
  highlightedMatchedFeatures?: string[];
  platform?: 'tiktok' | 'instagram' | 'youtube';
}

export function CreatorCard({
  creator,
  isSelected,
  searchId,
  onSelect,
  onRemove,
  onExclude,
  selectedSeedPostIds = [],
  onToggleSeedPost,
  seedPostLimitReached = false,
  highlightedMatchedFeatures = [],
}: Props) {
  const toPostPage = usePostNavigation();
  const [isMediaInView, setIsMediaInView] = useState(false);
  const mediaSectionRef = useIntersectionObserver(
    (entry) => {
      setIsMediaInView(entry.isIntersecting);
    },
    { threshold: 0.1 },
  );

  const formatNumber = (num: number) => {
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M`;
    }
    if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}K`;
    }
    return num.toString();
  };
  const highlightedFeatureSet = new Set(
    highlightedMatchedFeatures.map((feature) => feature.trim().toLowerCase()),
  );

  const PlatformLogo = PLATFORM_LOGO_BY_TYPE[creator.platform];

  return (
    <div
      className={cn(
        'bg-accent content-auto overflow-hidden rounded-lg border shadow',
        isSelected && 'outline-primary outline-2',
      )}
    >
      {/* Header Section */}
      <div className="border-border border-b p-4 pb-3">
        <div className="flex items-start justify-between">
          {/* Left: Profile Info */}
          <div className="flex gap-3">
            <div className="relative shrink-0">
              <ProfileImage
                src={creator.profile_image_url}
                name={creator.username}
                className="size-10 rounded-full object-cover"
              />
            </div>

            <div>
              <div className="mb-1 flex items-center gap-3">
                <div className="flex items-center gap-1">
                  <h3 className="text-foreground text-[15px] font-semibold">{creator.username}</h3>
                  <PlatformLogo className="size-3.5" />
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    window.open(
                      getPlatformUrl(creator.platform, creator.username),
                      '_blank',
                      'noopener,noreferrer',
                    );
                  }}
                  className="text-foreground hover:text-primary cursor-pointer transition-colors"
                  aria-label="View profile"
                >
                  <SquareArrowOutUpRight className="h-4 w-4" />
                </button>
              </div>

              {/* Stats Row */}
              <div className="flex items-center gap-4 text-xs">
                <div className="flex items-center gap-1">
                  <span className="text-foreground font-semibold">
                    {formatNumber(creator.followers)}
                  </span>
                  <span className="text-muted-foreground ml-1">Followers</span>
                </div>
                <TooltipFlat
                  content={
                    <>
                      <strong>Engagement Rate</strong>
                      <br />
                      [total engagement in last 30 days/ video count of last 30 days/followers]
                      <br />
                      <br />
                      <strong>Engagement</strong>
                      <br />
                      [likes + comments + shares]
                    </>
                  }
                >
                  <div className="flex items-center gap-1">
                    <span className="text-foreground font-semibold">
                      {formatNumber(Number(creator.average_er.toFixed(1)))}%
                    </span>
                    <span className="text-muted-foreground ml-1">Average ER</span>
                    <InfoIcon className="text-muted-foreground size-3" />
                  </div>
                </TooltipFlat>
              </div>

              {/* Attributes Row */}
              {creator.post_categories && creator.post_categories.length > 0 && (
                <div className="mt-2 flex items-center gap-1.5">
                  {creator.post_categories.slice(0, 3).map((attr) => (
                    <Badge key={attr} variant="outline" className="bg-transparent">
                      {attr}
                    </Badge>
                  ))}
                  {creator.post_categories.length > 3 && (
                    <span className="text-muted-foreground text-xs">
                      +{creator.post_categories.length - 3} more
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Right: Action Buttons */}
          <div className="flex items-center gap-2" data-no-propagate>
            {onExclude && (
              <Button
                variant="ghost"
                size="sm"
                className="h-9 px-3"
                onClick={(e) => {
                  e.stopPropagation();
                  onExclude();
                }}
              >
                Exclude
              </Button>
            )}
            {onRemove && (
              <Button
                variant="ghost"
                size="sm"
                className="h-9 px-3"
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove();
                }}
              >
                Remove
              </Button>
            )}
            <Button
              size="sm"
              variant="outline"
              className="h-9 px-3"
              onClick={(e) => {
                e.stopPropagation();
                trackEvent('Creator', 'select_creator', {
                  creator_id: creator.creator_id,
                  creator_name: creator.username,
                  search_id: searchId,
                });
                onSelect();
              }}
            >
              {isSelected ? 'Selected' : 'Select'}
            </Button>
          </div>
        </div>
      </div>

      {/* Content Grid */}

      {creator.matched_posts && (
        <div className="p-4" ref={mediaSectionRef}>
          <div className="overflow-x-auto pb-2">
            <div className="flex h-90 min-w-max gap-3">
              {isMediaInView &&
                creator.matched_posts?.map((content, idx) => {
                  const isSelectedPost = selectedSeedPostIds.includes(content.id);
                  const isDisabled = !isSelectedPost && seedPostLimitReached;

                  const actionButton = (
                    <Button
                      size="sm"
                      variant={isSelectedPost ? 'default' : 'secondary'}
                      className={cn(
                        'h-9 px-4 text-sm font-medium',
                        !isSelectedPost &&
                          'opacity-0 group-focus-within:opacity-100 group-hover:opacity-100',
                      )}
                      disabled={isDisabled}
                      onClick={(event) => {
                        event.stopPropagation();
                        onToggleSeedPost?.(content, creator.matched_keywords);
                      }}
                    >
                      {isSelectedPost ? 'Selected' : 'Find lookalike'}
                    </Button>
                  );

                  return (
                    <div
                      key={idx}
                      className="group relative w-52 cursor-pointer overflow-hidden rounded-xl"
                      onClick={() => toPostPage(content.id)}
                    >
                      {content.video_url ? (
                        <PostMedia
                          type="VIDEO"
                          videoSource={content.video_url}
                          posterUrl={content.poster_url}
                          controls={false}
                          showViewDetail={false}
                          asyncVideo
                          forcePlay={idx === 0}
                        />
                      ) : (
                        <PostMedia type="CAROUSEL" posterUrl={content.poster_url} />
                      )}
                      {isDisabled ? (
                        <TooltipFlat asChild content="You can select up to 6 posts">
                          <div className="absolute top-2 right-2 opacity-0 transition-opacity group-hover:opacity-100">
                            {actionButton}
                          </div>
                        </TooltipFlat>
                      ) : !isSelectedPost ? (
                        <div className="absolute top-2 right-2">{actionButton}</div>
                      ) : (
                        <div className="absolute top-2 right-2">{actionButton}</div>
                      )}
                      <div className="from-primary/80 absolute bottom-0 left-0 flex h-10 w-full items-center bg-linear-to-t to-transparent">
                        <div className="flex items-center gap-2 p-2 text-xs font-medium text-white">
                          <Eye className="size-3" />
                          {formatNumber(content.engagement.view)}
                          <Heart className="size-3" />
                          {formatNumber(content.engagement.like)}
                          <MessageCircle className="size-3" />
                          {formatNumber(content.engagement.comment)}
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
          {/* Match Info */}
          <div className="mt-3">
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-muted-foreground text-xs">AI matched:</span>
              {creator.matched_keywords?.map((feature) => {
                const isHighlighted = highlightedFeatureSet.has(feature.trim().toLowerCase());

                return (
                  <Badge
                    key={feature}
                    variant={isHighlighted ? 'default' : 'outline'}
                    className={cn('max-w-full', !isHighlighted && 'bg-transparent')}
                  >
                    <span className="truncate">{feature}</span>
                  </Badge>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
